import { assertNoCustomerDepositRetry } from "services/customerDepositRetry";
import { assertReservationDepositAvailable } from "services/unresolvedCustomerDeposits";
import { getConfig } from "config";
import { generateOtpHeader } from "services/lockerAuth";
import { requestLockerJson, requestLockerVoid } from "services/lockerClient";

type ReservationsFilter = {
  active?: boolean;
  code?: string;
};

export type CarrierValidationLockerCapability = Partial<Pick<Locker, "id" | "flags" | "offline">>;
export const CARRIER_VALIDATION_CAPABILITY_TIMEOUT_MS = 15 * 1000;

export const fetchLocker = async (): Promise<Locker> => {
  const { apiKey, lockerId } = getConfig();
  return requestLockerJson<Locker>(`lockers/${lockerId}/`, {
    headers: {
      "x-otp-code": generateOtpHeader(apiKey),
    },
    errorMessage: (response) => `Error fetching locker: ${response.statusText}`,
  });
};

export const fetchCarrierValidationLockerCapability = async ({
  signal,
}: {
  signal: AbortSignal;
}): Promise<CarrierValidationLockerCapability> => {
  const { apiKey, lockerId } = getConfig();
  const locker = await requestLockerJson<Locker>(`lockers/${lockerId}/`, {
    cache: "no-store",
    signal,
    headers: {
      "x-otp-code": generateOtpHeader(apiKey),
    },
    errorMessage: (response) => `Error fetching locker capability: ${response.statusText}`,
  });

  return {
    id: locker?.id,
    flags: locker?.flags,
    offline: locker?.offline,
  };
};

export const fetchReservations = async (
  filter?: ReservationsFilter
): Promise<PaginatedResponse<Reservation>> => {
  const { lockerId } = getConfig();
  return requestLockerJson<PaginatedResponse<Reservation>>("reservations/", {
    params: {
      locker_id: lockerId,
      active: filter?.active !== undefined ? filter.active.toString() : undefined,
      code: filter?.code,
      // Opt out of server-rendered base64 QR images; the app renders QR locally
      // from the code fields when needed (expedibox-admin#3933).
      include_qr_code_src: false,
    },
  });
};

export const createReservation = async (data: Reservation): Promise<Reservation> => {
  await assertReservationDepositAvailable(data);
  await assertNoCustomerDepositRetry(data);
  const { lockerId } = getConfig();
  return requestLockerJson<Reservation>("reservations/", {
    method: "POST",
    body: { ...data, locker_id: lockerId },
  });
};

export const updateReservation = async (data: Reservation): Promise<Reservation> => {
  await assertReservationDepositAvailable(data);
  await assertNoCustomerDepositRetry(data);
  const { lockerId } = getConfig();
  return requestLockerJson<Reservation>(`reservations/${data.id}/`, {
    method: "PATCH",
    body: { ...data, locker_id: lockerId },
  });
};

export const cancelReservation = async (id: string): Promise<void> => {
  await requestLockerVoid(`reservations/${id}/cancel`, {
    method: "POST",
  });
};

export const fetchRecipients = async (): Promise<Recipient[]> => {
  return requestLockerJson<Recipient[]>("recipients/");
};

export const createBulkReservation = async (data: BulkReservation): Promise<BulkReservation> => {
  // Reject before sending the batch, so a blocked parcel cannot partially mutate it.
  for (const reservation of data.reservations) {
    await assertReservationDepositAvailable(reservation);
    await assertNoCustomerDepositRetry(reservation);
  }
  const { lockerId } = getConfig();
  return requestLockerJson<BulkReservation>("reservationsBulk", {
    method: "POST",
    body: { ...data, locker_id: lockerId },
  });
};

export type { ReservationsFilter };
